import { useEffect, useState } from 'react';
import { animate, motion } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import { Avatar, EmptyState, Loader, Page, PageHead, Petals } from '../components/UI';
import { api } from '../utils/api';

const GLYPHS = ['♈︎', '♉︎', '♊︎', '♋︎', '♌︎', '♍︎', '♎︎', '♏︎', '♐︎', '♑︎', '♒︎', '♓︎'];

function Wheel() {
  return (
    <motion.div className="wheel" animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 60, ease: 'linear' }}>
      {GLYPHS.map((g, i) => (
        <span key={g} style={{ transform: `rotate(${i * 30}deg) translateY(-96px) rotate(${-i * 30}deg)` }}>{g}</span>
      ))}
    </motion.div>
  );
}

function Picker() {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    Promise.all([api('/interests?box=accepted'), api('/profiles/shortlist')])
      .then(([a, s]) => {
        const all = [...a.items.map((i) => i.profile), ...s.items];
        setItems(all.filter((p, i) => all.findIndex((x) => x.profileId === p.profileId) === i));
      })
      .catch(() => setItems([]));
  }, []);
  if (!items) return <Loader />;
  if (!items.length) return <EmptyState icon="moon" title="No one to match yet" text="Shortlist or connect with someone, then check your Kundli compatibility here. You can also open Kundli from any profile." action={<Link to="/matches" className="btn btn--primary">Find matches</Link>} />;
  return (
    <div className="kpicker">
      {items.map((p, i) => (
        <motion.button key={p.profileId} className="kpicker__item" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} whileHover={{ y: -4 }} onClick={() => navigate(`/kundli/${p.profileId}`)}>
          <Avatar src={p.photo} name={p.name} size={64} locked={p.photoLocked} />
          <strong>{p.name}</strong>
          <small className="muted">{p.age} yrs · {p.city}</small>
          <span className="link">Match Kundli →</span>
        </motion.button>
      ))}
    </div>
  );
}

function Bar({ k, i }) {
  const tint = k.score === k.max ? 'good' : k.score === 0 ? 'bad' : 'mid';
  return (
    <motion.div className="koota" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
      <div className="koota__top">
        <strong>{k.name}</strong>
        <span className={`koota__score koota__score--${tint}`}>{k.score}/{k.max}</span>
      </div>
      <small className="muted">{k.about}</small>
      <div className="koota__track">
        <motion.div className={`koota__fill koota__fill--${tint}`} initial={{ width: 0 }} whileInView={{ width: `${(k.score / k.max) * 100}%` }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.2 + i * 0.08 }} />
      </div>
    </motion.div>
  );
}

export default function Kundli() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (!id) return;
    api(`/profiles/${id}/kundli`).then(setD).catch((e) => setError(e.message));
  }, [id]);

  useEffect(() => {
    if (!d) return undefined;
    const c = animate(0, d.result.total, { duration: 1.6, ease: 'easeOut', onUpdate: (v) => setShown(Math.round(v)) });
    return () => c.stop();
  }, [d]);

  if (!id) {
    return (
      <Page>
        <div className="container">
          <PageHead eyebrow="Kundli" title="Check Guna Milan" sub="Choose a connection or shortlisted profile to see your Ashtakoot compatibility." />
          <Picker />
        </div>
      </Page>
    );
  }
  if (error) return <Page><div className="container"><EmptyState icon="moon" title="Couldn't load Kundli" text={error} /></div></Page>;
  if (!d) return <Page><Loader label="Reading the stars…" /></Page>;

  const r = d.result;
  const missing = !d.me.horoscope?.rashi;
  return (
    <Page className="kundli">
      <section className="kundli__hero">
        <Petals count={10} />
        <div className="container kundli__pair">
          <motion.div className="kundli__person" initial={{ x: -60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 120 }}>
            <Avatar src={d.me.photo} name={d.me.name} size={96} />
            <strong>You</strong>
            <small>{d.me.horoscope?.rashi || 'Rashi not set'}</small>
          </motion.div>
          <div className="kundli__center">
            <Wheel />
            <div className="kundli__score">
              <motion.strong initial={{ scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.2 }}>{shown}</motion.strong>
              <span>of 36 gunas</span>
            </div>
          </div>
          <motion.div className="kundli__person" initial={{ x: 60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 120 }}>
            <Avatar src={d.them.photo} name={d.them.name} size={96} locked={d.them.photoLocked} />
            <strong>{d.them.firstName}</strong>
            <small>{d.them.horoscope?.rashi || 'Rashi not set'}</small>
          </motion.div>
        </div>
        <motion.div className="kundli__verdict" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6 }}>
          <Icon name="sparkle" size={18} /> {r.verdict}
        </motion.div>
      </section>
      <div className="container narrow">
        {missing && (
          <div className="note">
            <Icon name="moon" size={16} /> Add your rashi, nakshatra and birth details for a more accurate match. <Link to="/me/edit?tab=horoscope">Add horoscope</Link>
          </div>
        )}
        <div className="kootas">
          {r.kootas.map((k, i) => <Bar key={k.name} k={k} i={i} />)}
        </div>
        <div className="manglik">
          <div><small className="muted">Your Manglik status</small><strong>{r.manglik[0]}</strong></div>
          <Icon name="heart" size={20} />
          <div><small className="muted">{d.them.firstName}'s Manglik status</small><strong>{r.manglik[1]}</strong></div>
        </div>
        <p className="muted small center">Guna Milan is a traditional guide, not a guarantee. Many happy couples score below 18 — talk, meet families, and trust your judgement.</p>
        <div className="center row-actions">
          <Link to={`/profile/${d.them.profileId}`} className="btn btn--ghost">View profile</Link>
          <Link to={`/messages/${d.them.profileId}`} className="btn btn--primary"><Icon name="chat" size={16} /> Message</Link>
        </div>
      </div>
    </Page>
  );
}
